import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for microphone audio input for Morse code practice.
 * 
 * NOTE: This feature may not work with all microphone hardware/drivers.
 * Known issues:
 * - Intel Smart Sound Technology (Intel SST) microphones may send silent data (all 128s)
 * - Some laptop built-in microphones may not work properly
 * 
 * Recommended alternatives:
 * - Use an external USB microphone
 * - Use a headset with microphone
 * - Try a different browser (Firefox vs Chrome)
 */
export const useAudioInput = (wpm, onSignal) => {
    const [isListening, setIsListening] = useState(false);
    const [volume, setVolume] = useState(0);
    const [threshold, setThreshold] = useState(20);
    const [isSignalActive, setIsSignalActive] = useState(false);
    const [audioContextState, setAudioContextState] = useState('inactive');

    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const rafRef = useRef(null);
    const signalStartRef = useRef(null);
    const streamRef = useRef(null);

    const thresholdRef = useRef(threshold);
    const wpmRef = useRef(wpm);
    const onSignalRef = useRef(onSignal);

    useEffect(() => { thresholdRef.current = threshold; }, [threshold]);
    useEffect(() => { wpmRef.current = wpm; }, [wpm]);
    useEffect(() => { onSignalRef.current = onSignal; }, [onSignal]);

    const analyze = useCallback(() => {
        if (!analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.fftSize);
        analyserRef.current.getByteTimeDomainData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
            const amplitude = (dataArray[i] - 128) / 128;
            sum += amplitude * amplitude;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const normalizedVolume = Math.min(100, rms * 400);

        setVolume(normalizedVolume);

        if (normalizedVolume > thresholdRef.current) {
            if (!signalStartRef.current) {
                signalStartRef.current = Date.now();
                setIsSignalActive(true);
            }
        } else {
            if (signalStartRef.current) {
                const duration = Date.now() - signalStartRef.current;
                signalStartRef.current = null;
                setIsSignalActive(false);

                const unitMs = 1200 / wpmRef.current;
                if (duration > 30) {
                    const dotDashThreshold = unitMs * 2;
                    if (duration < dotDashThreshold) {
                        onSignalRef.current('dot');
                    } else {
                        onSignalRef.current('dash');
                    }
                }
            }
        }

        rafRef.current = requestAnimationFrame(analyze);
    }, []);

    const startListening = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
            });
            streamRef.current = stream;

            const AudioContext = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContext();

            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }
            setAudioContextState(audioContextRef.current.state);

            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 2048;
            analyserRef.current.smoothingTimeConstant = 0;

            sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
            sourceRef.current.connect(analyserRef.current);

            setIsListening(true);
            analyze();
        } catch (err) {
            console.error("Error accessing microphone:", err);
            setIsListening(false);
            setAudioContextState('error');
        }
    }, [analyze]);

    const stopListening = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (sourceRef.current) sourceRef.current.disconnect();
        if (analyserRef.current) analyserRef.current.disconnect();
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(() => { });
            setAudioContextState('closed');
        }

        setIsListening(false);
        setVolume(0);
        setIsSignalActive(false);
    }, []);

    useEffect(() => {
        return () => stopListening();
    }, [stopListening]);

    return {
        isListening,
        startListening,
        stopListening,
        volume,
        threshold,
        setThreshold,
        isSignalActive,
        audioContextState
    };
};
